import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, TrendingUp, Users, Target, Share2, Handshake, Megaphone,
  Network, Cpu, Rocket, DollarSign, Repeat, Award, CheckCircle2,
} from "lucide-react";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SEOHead } from "@/features/shared/SEOHead";

// Partner-first careers page. Frayze pays for performance, not seats: a high share of every
// build fee closed, a ramp residual on new MRR, and a lifetime residual on managed accounts.
// No invented client metrics; founder framing only (see proof-integrity). Commission % carried
// from the prior live page - confirm/adjust before relying on them contractually.

const COMMISSION_BUILD = "35%";
const RESIDUAL_RAMP = "20%";
const RESIDUAL_LIFETIME = "10%";

const roles = [
  {
    title: "Commission Sales / Closer",
    icon: Target,
    badges: ["Remote", "Canada + U.S.", "Commission-Only"],
    tagline: "Find business owners who are bleeding leads, show them the fix, close the deal.",
    earn: `${COMMISSION_BUILD} of every build fee you close, plus ${RESIDUAL_RAMP} of MRR through the account's first months and a ${RESIDUAL_LIFETIME} lifetime residual if you keep managing it.`,
    isJob: true,
  },
  {
    title: "Account Manager / Client Partner",
    icon: Handshake,
    badges: ["Remote", "Book of Business", "Residual Income"],
    tagline: "Own a book of clients. Keep them live, growing, and renewing, and get paid every month they stay.",
    earn: `A ${RESIDUAL_LIFETIME} monthly residual on the recurring revenue of every account you manage, for as long as you manage it. The bigger your book, the more it stacks.`,
    isJob: true,
  },
  {
    title: "Referral Partner",
    icon: Share2,
    badges: ["Any Market", "No Selling", "Get Paid on Closes"],
    tagline: "You know people who need what we build. Make the intro. We'll do the rest.",
    earn: "A commission on every referral that closes. No discovery calls, no managing the account. Just open the door and collect.",
    isJob: false,
  },
  {
    title: "Affiliate",
    icon: Megaphone,
    badges: ["Creators", "Consultants", "Operators with Reach"],
    tagline: "Promote Frayze to your audience and earn on every signup you drive.",
    earn: "An affiliate commission on signups from your link or code. Built for people with an audience, a list, or a community of business owners.",
    isJob: false,
  },
];

const fit = [
  {
    icon: Network,
    title: "You know business owners and managers",
    body: "You're a center of influence in your market. People take your call and trust your recommendation: that's the whole game.",
  },
  {
    icon: Users,
    title: "You have real public influence",
    body: "A network, an audience, a reputation. When you point people somewhere, they actually go.",
  },
  {
    icon: Cpu,
    title: "You're tech-oriented",
    body: "You get AI, automation, and CRM, and you can explain why they matter to a busy owner without drowning them in jargon.",
  },
  {
    icon: Rocket,
    title: "You're a self-starter",
    body: "Nobody hands you a lead list or a script to hide behind. You build the pipeline and you close it.",
  },
  {
    icon: Repeat,
    title: "You think in relationships, not transactions",
    body: "The real money is in the accounts you keep, not the deals you flip. You play the long game.",
  },
  {
    icon: TrendingUp,
    title: "You want uncapped upside",
    body: "A salary ceiling bores you. You'd rather own your results and get paid on what you actually produce.",
  },
];

const earn = [
  {
    icon: DollarSign,
    title: "Build-fee commission",
    body: `A high share (${COMMISSION_BUILD}) of every Required Build & Onboarding fee you close. Bigger builds, bigger checks. Paid up front.`,
  },
  {
    icon: TrendingUp,
    title: "Ramp residual",
    body: `${RESIDUAL_RAMP} of the recurring revenue on every new account through its first months, so a fast start pays you fast, not someday.`,
  },
  {
    icon: Repeat,
    title: "Lifetime residual",
    body: `A ${RESIDUAL_LIFETIME} monthly residual for as long as you manage the account. Build a book once, get paid on it for years.`,
  },
];

const Careers = () => {
  const apply = (subject: string) =>
    window.open(`mailto:careers@frayze.ca?subject=${encodeURIComponent(subject)}`, "_blank");

  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://frayze.ca/" },
      { "@type": "ListItem", position: 2, name: "Careers", item: "https://frayze.ca/careers/" },
    ],
  };

  // JobPosting schema only for the two contractor roles (commission-only → baseSalary omitted).
  const jobSchema = roles
    .filter((r) => r.isJob)
    .map((r) => ({
      "@context": "https://schema.org",
      "@type": "JobPosting",
      title: r.title,
      description: `${r.tagline} ${r.earn}`,
      identifier: {
        "@type": "PropertyValue",
        name: "Frayze Technologies Inc.",
        value: r.title.replace(/\s+/g, "-").toLowerCase(),
      },
      datePosted: "2026-06-01",
      validThrough: "2026-12-31",
      employmentType: "CONTRACTOR",
      hiringOrganization: {
        "@type": "Organization",
        name: "Frayze Technologies Inc.",
        sameAs: "https://frayze.ca",
        logo: "https://frayze.ca/Frayze%20Logo%20New%20light%20-%20landscape%20.png",
      },
      jobLocation: {
        "@type": "Place",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Thunder Bay",
          addressRegion: "ON",
          addressCountry: "CA",
        },
      },
      jobLocationType: "TELECOMMUTE",
    }));

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Frayze Technologies Inc.",
    url: "https://frayze.ca",
    logo: "https://frayze.ca/Frayze%20Logo%20New%20light%20-%20landscape%20.png",
    sameAs: [
      "https://www.linkedin.com/company/frayze/",
      "https://facebook.com/frayze.ca",
      "https://instagram.com/frayze.ca",
      "https://x.com/FrayzeIncCanada",
      "https://youtube.com/@frayzetech",
    ],
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, breadcrumbList, ...jobSchema],
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SEOHead
        title="Partner With Frayze | Commission Sales & Referral Partners"
        description="No salary cap, no clock. Frayze pays partners a high share of every build fee plus a monthly residual on accounts you manage. For closers, account managers, referrers, and affiliates."
        keywords="commission only sales jobs Canada, account manager residual income, referral partner program, affiliate marketing agency, remote sales partner, AI CRM sales"
        ogTitle="Partner With Frayze | Get Paid Like a Partner, Not an Employee"
        ogDescription="High commission on every build you close, plus a monthly residual on every account you manage. Closers, account managers, referrers, and affiliates wanted."
        canonical="https://frayze.ca/careers/"
        robots="index, follow, max-snippet:-1, max-image-preview:large"
        structuredData={combinedSchema}
      />

      <main className="pt-24 md:pt-32">
        <div className="container mx-auto px-4 mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Careers</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* HERO */}
        <section className="bg-gradient-hero text-white py-20 rounded-3xl mx-4 mb-16 relative overflow-hidden">
          <div className="container mx-auto px-4 text-center relative z-10 max-w-4xl">
            <span className="inline-block mb-5 px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase bg-white/15">
              Partner with Frayze
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-balance">
              Get Paid Like a Partner. Not an Employee.
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-balance">
              High commission on every build you close, plus a monthly residual for as long as you
              manage the account. No ceiling, no clock, no asking permission to earn more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" className="text-lg px-8" onClick={() => apply("Frayze Partner Application")}>
                See If You're a Fit <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white"
                onClick={() => apply("Refer a Business to Frayze")}
              >
                Refer a Business
              </Button>
            </div>
          </div>
        </section>

        {/* HONEST STATUS */}
        <section className="pb-4">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto p-5 rounded-xl border bg-muted/40 text-center">
              <p className="text-base md:text-lg">
                <span className="font-bold text-foreground">Frayze runs lean and pays for performance.</span>{" "}
                We don't have salaried seats open today, but if you can sell, manage relationships,
                or open doors, there is uncapped money here right now. Looking for a salaried role?{" "}
                <button onClick={() => apply("Resume on File - Future Roles")} className="underline font-semibold text-primary">
                  We still collect resumes
                </button>.
              </p>
            </div>
          </div>
        </section>

        {/* ARE YOU A FIT */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-balance">Are You a Fit?</h2>
              <p className="text-lg text-muted-foreground text-balance">
                This isn't for everyone, and it's not meant to be. The partners who win at Frayze
                have most of these. If that's you, keep reading. If it's not, no hard feelings.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {fit.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="flex gap-4 p-6 rounded-xl border bg-card">
                    <div className="shrink-0 w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold mb-1.5">{f.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* HOW YOU GET PAID */}
        <section className="py-16 bg-muted/20 border-y">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase bg-primary/10 text-primary">
                <Award className="w-3.5 h-3.5" /> Uncapped
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-balance">How You Get Paid</h2>
              <p className="text-lg text-muted-foreground text-balance">
                We pay partners more than agencies pay employees, because you carry the relationship
                and the upside. Three ways to stack income:
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-8">
              {earn.map((e) => {
                const Icon = e.icon;
                return (
                  <Card key={e.title} className="p-6 h-full">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">{e.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{e.body}</p>
                  </Card>
                );
              })}
            </div>
            <div className="max-w-3xl mx-auto p-5 rounded-xl bg-card border">
              <p className="font-semibold mb-1">Illustrative example: close one account.</p>
              <p className="text-sm text-muted-foreground">
                A $3,500 build at {COMMISSION_BUILD} pays you about $1,225 up front. Keep managing
                that client on a monthly plan and you earn a residual every month they stay, and
                every new account you add stacks on top. There's no cap on how many you carry.
              </p>
            </div>
          </div>
        </section>

        {/* ROLES */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-balance">Ways to Partner</h2>
              <p className="text-lg text-muted-foreground text-balance">
                Sell, manage, refer, or promote: pick the lane that fits how you already operate.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {roles.map((r) => {
                const Icon = r.icon;
                return (
                  <Card key={r.title} className="flex flex-col h-full">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{r.title}</CardTitle>
                      <CardDescription>
                        {r.badges.map((b, i) => (
                          <Badge key={i} variant={i === 2 ? "secondary" : "outline"} className="mr-2 mb-1 mt-2">{b}</Badge>
                        ))}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1">
                      <p className="font-medium mb-3">{r.tagline}</p>
                      <div className="flex gap-2 mb-5">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <p className="text-sm text-muted-foreground leading-relaxed">{r.earn}</p>
                      </div>
                      <Button className="w-full mt-auto" onClick={() => apply(`${r.title} - Frayze Partner Application`)}>
                        Apply for This Track <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* RESUME COLLECTION */}
        <section className="py-16 bg-muted/20 border-y">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4 text-balance">
                No fit yet? We're still meeting talent.
              </h2>
              <p className="text-lg text-muted-foreground mb-8 text-balance">
                We grow fast and our needs change. Send your resume and we'll keep it on file: when
                we open a salaried role, the people already on our radar get the first call.
              </p>
              <Button size="lg" variant="outline" onClick={() => apply("Resume on File - Future Roles")}>
                Send Your Resume <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-hero text-white">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">
              Ready to Own Your Book?
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-balance">
              Frayze is led by operators who built and ran lead systems inside a multi-rooftop auto
              group before starting the agency. We know what a closed deal is worth, and we share it.
            </p>
            <Button size="lg" variant="secondary" className="text-lg px-8" onClick={() => apply("Frayze Partner Application")}>
              Apply to Partner <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-6 text-lg opacity-90">careers@frayze.ca</p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Careers;
